import {
  DEFAULT_OUTBOUND_ID_NAME,
  DIRECT_LINE_NAME,
  DYNAMIC_CALLER_ID_NAME,
  DYNAMIC_CALLER_ID_NUMBER,
} from './constants';
import strings from './strings';
import { TenantPhoneNumberItem } from './types';

export const getAreaCode = (phoneNumber: string): string | null => {
  if (!phoneNumber.startsWith('+') || phoneNumber.length < 4) {
    return null;
  }

  const areaCode = phoneNumber.slice(2, 5) + '';
  return areaCode;
};

export const normilizePhone = (phone?: string) => {
  if (!phone) return '';
  const phoneNumber = phone.replace(/\D/g, '');
  return phoneNumber;
};

const sortByName = (a: TenantPhoneNumberItem, b: TenantPhoneNumberItem) => {
  const aName = a.name?.toLowerCase() ?? '';
  const bName = b.name?.toLowerCase() ?? '';
  const aCampaignName = a.campaignName?.toLowerCase() ?? '';
  const bCampaignName = b.campaignName?.toLowerCase() ?? '';

  // Prioritize items with names over those without names
  if (aName && !bName) return -1; // `a` has a name, `b` does not
  if (!aName && bName) return 1; // `b` has a name, `a` does not

  // If both have names, sort alphabetically by name
  if (aName < bName) return -1;
  if (aName > bName) return 1;

  // If names are equal or missing, sort by campaign name
  if (aCampaignName < bCampaignName) return -1;
  if (aCampaignName > bCampaignName) return 1;

  return 0; // If both name and campaign name are equal
};

const directNumberItem = {
  id: DYNAMIC_CALLER_ID_NUMBER,
  number: DYNAMIC_CALLER_ID_NUMBER,
  name: DYNAMIC_CALLER_ID_NAME,
  selected: false,
  tooltip: strings.dynamicCallerIdTooltip,
};

/**
 * Generates a list of phone number items with metadata and determines the selected item.
 *
 * @param {Object} params - The parameters for generating phone number items.
 * @param {any[]} params.data - The list of phone numbers to process.
 * @param {string} params.selectedLine - The currently selected phone number.
 * @param {boolean} [params.isDynamic=false] - Whether dynamic caller ID is enabled.
 * @param {string} [params.directLine] - The direct line phone number, if available.
 * @param {string} [params.outgoingCallerId] - Otgoung caller ID, if available.

 * @returns {Object} - An object containing:
 *   - `items`: An array of `TenantPhoneNumberItem` objects, each representing a phone number with metadata.
 *   - `selectedItem`: The currently selected `TenantPhoneNumberItem`, if any.
 *
 * The function processes the input `data` to:
 * - Add metadata (e.g., `id`, `selected`) to each phone number.
 * - Add a dynamic caller ID item if `isDynamic` is true.
 * - Add the `outgoingCallerId` if passed as a special item at the end of the list.
 * - Determine the currently selected item based on the `selectedLine`.
 * - Add the `directLine` if passed as a special item at the beginning of the list.
 * - Sort the items alphabetically by name and campaign name.

 */
export const getPhomeNumberItems = ({
  data = [],
  selectedLine,
  isDynamic = false,
  directLine,
  outgoingCallerId,
}: {
  data: any[];
  selectedLine: string;
  isDynamic?: boolean;
  directLine?: string;
  outgoingCallerId?: string;
}): {
  items: TenantPhoneNumberItem[];
  selectedItem?: TenantPhoneNumberItem;
} => {
  let selectedItem: TenantPhoneNumberItem | undefined;
  let items = [];
  let outgoingCallerIdFound: TenantPhoneNumberItem | undefined;
  if (data.length > 0) {
    items = data.reduce((acc, item) => {
      const el = {
        ...item,
        id: item.number,
        selected: item.number === selectedLine,
      };

      if (el.selected) {
        selectedItem = el;
      }

      if (item.number === directLine) {
        return acc;
      }

      if (outgoingCallerId && item.number === outgoingCallerId) {
        outgoingCallerIdFound = el;
        return acc;
      }

      acc.push(el);
      return acc;
    }, [] as any[]);

    items.sort(sortByName);
  }

  if (directLine) {
    const directLineItem = {
      id: directLine,
      number: directLine,
      name: DIRECT_LINE_NAME,
      selected: directLine === selectedLine,
    };
    if (directLineItem.selected) {
      selectedItem = directLineItem;
    }
    items.unshift(directLineItem);
  }

  if (isDynamic) {
    const dynamicItem = {
      ...directNumberItem,
      selected: selectedLine === DYNAMIC_CALLER_ID_NUMBER,
    };
    if (dynamicItem.selected) {
      selectedItem = dynamicItem;
    }
    items.unshift(dynamicItem);
  }

  if (outgoingCallerIdFound) {
    const el = {
      ...outgoingCallerIdFound,
      name: outgoingCallerIdFound.name ?? DEFAULT_OUTBOUND_ID_NAME,
    };
    items.unshift(el);
  } else if (outgoingCallerId) {
    const outgoingCallerIdItem = {
      id: outgoingCallerId,
      number: outgoingCallerId,
      name: DEFAULT_OUTBOUND_ID_NAME,
      selected: outgoingCallerId === selectedLine,
    };
    if (outgoingCallerIdItem.selected) {
      selectedItem = outgoingCallerIdItem;
    }
    items.unshift(outgoingCallerIdItem);
  }

  return { items, selectedItem };
};
